import React from "react";
import { twMerge } from "tailwind-merge";
import { Text, Button, Icon } from "@virkesborsen/ui-component-library";
import { DynamicModal } from "../../../uicomponents/DynamicModal";
import { useMetricData } from "../hooks/UseMetricData";
import { useSubscription } from "../../context/SubscriptionCheckoutContext";

export default function WidgetWrapper({
  title = "Example title",
  infoContent,
  editDialog,
  className = "",
  children,
  ...props
}) {
  const [showInfoModal, setShowInfoModal] = React.useState(false);
  const [showEditDialog, setShowEditDialog] = React.useState(false);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const { openCheckout } = useSubscription();
  const { propDfr } = useMetricData();

  const handleEditSubmit = async () => {
    if (!editDialog?.onSubmit) return;

    setIsSubmitting(true);
    try {
      await editDialog.onSubmit();
      setShowEditDialog(false);
    } catch (error) {
      console.error("Error submitting edit form:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenSubscriptionCheckout = () => {
    openCheckout();
  };

  const handleUserSubscription = (onPlus, onLocked, extraCondition = null) => {
    const hasSubscription = propDfr || extraCondition;

    return hasSubscription ? onPlus() : onLocked();
  };

  const hasInfo = !!infoContent;
  const hasEditDialog = !!editDialog;

  return (
    <>
      <div
        className={twMerge(
          "rounded-2xl border-solid border-1 border-[#e6e6e6]",
          className
        )}
        {...props}
      >
        {/* Header */}
        <div className="flex align-center justify-between p-4 pb-2">
          <div className="flex gap-2 items-center">
            <Text variant="text-sm/leading-normal/medium">{title}</Text>
            {hasInfo && (
              <Button
                tw
                onClick={() => setShowInfoModal(true)}
                size="icon"
                variant="tertiary"
                aria-label="Visa information"
              >
                <Icon tw name="InfoOutlined" size="medium" />
              </Button>
            )}
          </div>

          {hasEditDialog && (
            <Button
              tw
              variant="secondary"
              size="small"
              onClick={() => {
                handleUserSubscription(
                  () => setShowEditDialog(true),
                  () => {
                    handleOpenSubscriptionCheckout();
                  },
                  null
                );
              }}
            >
              {editDialog.title || "Ändra"}
            </Button>
          )}
        </div>

        {children}
      </div>

      {hasEditDialog && (
        <DynamicModal
          open={showEditDialog}
          onOpenChange={setShowEditDialog}
          bp={{ sm: "drawer", lg: "dialog" }}
          header={
            <div className="flex w-full items-center justify-between">
              <Text variant="text-sm/leading-normal/medium">
                {editDialog.title || "Ändra"}
              </Text>
              <Button
                tw
                variant="tertiary"
                size="icon"
                onClick={() => setShowEditDialog(false)}
                aria-label="Stäng"
              >
                <Icon
                  tw
                  name="CrossSimple"
                  className="color-charcoal-500"
                  size="large"
                />
              </Button>
            </div>
          }
          footer={
            <Button
              tw
              variant="primary"
              onClick={handleEditSubmit}
              disabled={isSubmitting || !editDialog.onSubmit}
              fullWidth
            >
              {editDialog.submitLabel || "Spara"}
              {isSubmitting && (
                <div className="size-4 border-2 border-white border-t-transparent border-solid rounded-full animate-spin"></div>
              )}
            </Button>
          }
        >
          {editDialog.content?.(isSubmitting)}
        </DynamicModal>
      )}

      {/* Info Modal */}
      {hasInfo && (
        <DynamicModal
          open={showInfoModal}
          onOpenChange={setShowInfoModal}
          bp={{ sm: "drawer", lg: "sheet" }}
          header={
            <div className="flex justify-between w-full">
              <Text variant="text-lg/leading-normal/medium">{title}</Text>
              <Button
                tw
                onClick={() => setShowInfoModal(false)}
                size="icon"
                variant="tertiary"
                aria-label="Stäng"
              >
                <Icon name="CrossSimple" size="large" />
              </Button>
            </div>
          }
          footer={
            <Button
              tw
              variant="primary"
              fullWidth
              onClick={() => setShowInfoModal(false)}
            >
              Stäng
            </Button>
          }
        >
          {infoContent}
        </DynamicModal>
      )}
    </>
  );
}
